import { h, Component } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import { isDirectTicketingEnabled } from 'store/ticketingPublic';
import pure from 'utils/pure';
import { accessNested, joinClasses, isObject } from 'utils/utils';
import { trackNearestShowtimeTicketClick } from 'utils/clientUtils';
import formatDateTime from 'utils/formatDateTime';
import { track, updateDataLayerScreening } from 'utils/tracking';
import { Link } from 'components/core/link/link';
import * as svg from 'components/shared/svgIcons/svgIcons.js';
import s from 'components/shared/theaters/screening.sss';


export default @pure class Screening extends Component {

	///////////////////////
	//// LIFE CYCLE ///////
	///////////////////////

	constructor() {
		super();

		this.selectScreening         = this.selectScreening.bind(this);
		this.selectTime              = this.selectTime.bind(this);
		// this.toggleFavorite          = this.toggleFavorite.bind(this);
		// this.toggleExpanded          = this.toggleExpanded.bind(this);
		this.selectScreeningDeepLink = this.selectScreeningDeepLink.bind(this);
		this.selectDirectTicketing   = this.selectDirectTicketing.bind(this);
		this.showAuthModal           = this.showAuthModal.bind(this);
	}

	componentDidUpdate(prevProps) {
		let link = this.$link;

		if (link && !link.style) {
			// We need to grab the actual dom element instead of the Component
			link = link.base;
		}

		if (link && !this.props.appData.options.useProviderInterstitial && prevProps.provider !== this.props.provider) {
			link.style.opacity = 0;
			link.offsetWidth;
			link.style.opacity = '';
		}
	}

	getTime(screening, copy, ctaCopy, date) {
		let time = '';
		if (screening.cta || !screening.time) {
			const ctaVal = ctaCopy[screening.cta || 0];
			time = copy[ctaVal] || ctaVal;
		} else {
			const timeNumbers = formatDateTime(screening.datetime, copy.$TIME_FORMAT, copy.$DATETIME_LOCALE);
			let timePeriod = formatDateTime(screening.datetime, copy.$TIME_PERIOD, copy.$DATETIME_LOCALE);
			if (screening.nextDayDatetime && +screening.stDate.date === +date) {
				timePeriod += ' (' + formatDateTime(screening.nextDayDatetime, copy.$DAY_FORMAT, copy.$DATETIME_LOCALE) + ')';
			}
			time = [timeNumbers, (<span class={s.period}>{timePeriod}</span>)];
		}
		return time;
	}

	getCustomClasses(screening){
		let customClasses = [];
		if (screening.customClasses) {
			customClasses = customClasses.concat(screening.customClasses).map(c => s[c]);
		}
		return customClasses;
	}


	///////////////////////
	//// CLICK HANDLERS ///
	///////////////////////

	selectTime(event) {
		const { theater, screening } = this.props;
		Store.emit(actions.SELECT_TIME, theater, screening, event);
	}

	selectScreening(event) {
		const { screening, theater } = this.props;
		const { exhibitor } = theater;

		this.selectTime(event);
		Store.emit(actions.SELECT_SCREENING, { screening: screening, theater: theater });

		track({ a: 'get_tickets_exhibitor', l: screening.theater_id, useCustomPageTrack: true });
		track({ a: 'open_provider_interstitial', useCustomPageTrack: true });

		if (screening.stDate) {
			track({ a: 'ticket_click_day', l: screening.stDate.date.getDay(), useCustomPageTrack: true });
		}
		if (screening.time24) {
			track({ a: 'ticket_click_time', l: screening.time24, useCustomPageTrack: true });
		}
		if (exhibitor && exhibitor.domain) {
			track({ a: 'ticket_click_chain', l: exhibitor.domain, useCustomPageTrack: true });
		}
	}

	selectScreeningDeepLink(event) {
		const { screening, theater, copy, pageData } = this.props;
		const { exhibitor } = theater;

		let cta;
		if (isObject(pageData.ctaCopy) && isObject(copy)) {
			const ctaVal = pageData.ctaCopy[screening.cta];
			cta = copy[ctaVal] || ctaVal;
		}

		this.selectTime(event);
		updateDataLayerScreening(screening, theater, 'ticket_click', event.target.href, cta);

		track({ a: 'get_tickets_exhibitor', l: screening.theater_id, useCustomPageTrack: true });
		track({ a: 'ticket_click', l: screening.id, useCustomPageTrack: true });

		if (screening.customTracking) {
			track({ a: screening.customTracking });
		}
		if (screening.stDate) {
			track({ a: 'ticket_click_day', l: screening.stDate.date.getDay(), useCustomPageTrack: true });
		}
		if (screening.time24) {
			track({ a: 'ticket_click_time', l: screening.time24, useCustomPageTrack: true });
		}
		if (exhibitor && exhibitor.domain) {
			track({ a: 'ticket_click_chain', l: exhibitor.domain, useCustomPageTrack: true });
		}
		if (this.provider && screening.urls[this.provider]) {
			track({ a: 'ticket_provider', l: this.provider, useCustomPageTrack: true });

			const providers = Object.keys(screening.urls);

			const val = providers.map(p => {
				const active = p === this.provider;
				if (p === 'cinema' && exhibitor) p += ':' + exhibitor.domain.slice(0, 50);
				return active ? '*' + p : p;
			}).join('|').slice(0, 250);

			track({ a: 'ticket_provider_top_choices', l: val, useCustomPageTrack: true });
		}

		trackNearestShowtimeTicketClick(theater);

		Store.emit(actions.TICKET_LINK_CLICKED, screening, theater, event, this.provider);
		return true;
	}

	selectDirectTicketing(event) {
		const { theater, screening } = this.props;
		this.selectScreening(event);
		Store.emit(actions.OPEN_TICKETING, { theater, screening });
	}

	showAuthModal() {
		const { screening, theater } = this.props;
		const { exhibitor } = theater;

		Store.emit(actions.SHOW_SCREENING_AUTH, { screening: screening, theater: theater });

		track({ a: 'get_tickets_exhibitor', l: screening.theater_id, useCustomPageTrack: true });
		track({ a: 'open_auth' });

		if (screening.stDate) {
			track({ a: 'ticket_click_day', l: screening.stDate.date.getDay(), useCustomPageTrack: true });
		}
		if (screening.time24) {
			track({ a: 'ticket_click_time', l: screening.time24, useCustomPageTrack: true });
		}
		if (exhibitor && exhibitor.domain) {
			track({ a: 'ticket_click_chain', l: exhibitor.domain, useCustomPageTrack: true });
		}
	}

	filterProvidersListPerTheater(theater, providersList) {
		let providersToHidePerTheater = accessNested(this.props, 'pageData.hideProvidersForTheater');

		if (providersToHidePerTheater) {
			Object.keys(providersToHidePerTheater).forEach((key) => {
				if (key === theater.pid) {
					let providersArray = providersToHidePerTheater[key].providers.split(',');
					providersList = providersList.filter(p => providersArray.indexOf(p) === -1);
				}
			});
		}

		return providersList;
	}

	///////////////////////
	//// RENDER METHODS ///
	///////////////////////

	renderInterstitialButton(classes, displayContent) {
		return (
			<div class={classes} onClick={this.selectScreening}>{displayContent}</div>
		);
	}

	renderDirectLink(classes, displayContent, url) {
		return (
			<Link
				class={classes}
				ref={ref => this.$link = ref}
				name={`ticket_link${this.provider ? '_' + this.provider : ''}`}
				href={url}
				trackLabel={url}
				target={this.props.appData.options.loadShowtimesLinksInPage ? '_self' : '_blank'}
				extraOnClick={this.selectScreeningDeepLink}
				renderCustomChild
			>
				{displayContent}
			</Link>
		);
	}

	renderDirectTicketingButton(classes, displayContent) {
		return (
			<div class={classes} onClick={this.selectDirectTicketing}>{displayContent}</div>
		);
	}

	renderAuthButton(classes, displayContent) {
		return (
			<div class={classes} onClick={this.showAuthModal}>{displayContent}</div>
		);
	}

	render(props) {
		const {
			appData, // object
			device,  // string
			isIOS, // boolean
			provider, // string
			screening, // object
			theater, // object
			copy, // object
			date, // string
			pageData = {}, // object
		} = props;

		const ctaCopy = pageData.ctaCopy || accessNested(appData, 'pages.showtimes.data.ctaCopy') || {};

		const time = this.getTime(screening, copy, ctaCopy, date, device);

		const urls = screening.urls;
		let url = '';

		let provPriorities = [provider, 'fandango', 'movietickets', 'atom', 'cinema'];

		const classes = [
			s.wrapper,
			device === 'desktop' && s.desktop,
			!isIOS && s.noIOS,
		].concat(props.class).filter(el => el).join(' ');

		const useAuth = !!accessNested(pageData, 'auth.providerId', '');

		delete props.class;
		let providersList = Object.keys(urls);
		if (screening.disabledProviders) {
			providersList = providersList.filter(provider => screening.disabledProviders.indexOf(provider) === -1);
			provPriorities = provPriorities.filter(provider => providersList.indexOf(provider) !== -1);
		}

		providersList = this.filterProvidersListPerTheater(theater, providersList);

		const defaultProvider = pageData.defaultProvider;
		if (providersList.indexOf(defaultProvider) !== -1) {
			providersList = [defaultProvider];
		}

		const bestProvider = provPriorities.find(p => p && providersList.indexOf(p) > -1);
		this.provider = bestProvider;
		if (bestProvider) url = urls[bestProvider];

		const useDirectTicketing = isDirectTicketingEnabled(theater);
		if (useDirectTicketing && /*providersList.length >= 1 &&*/ Object.keys(urls).length > 1 && !urls.cinema) {
			urls.cinema = '#';
			providersList.push('cinema');
		}
		const hasProviders = providersList.length > 1;

		const availableInterstitial = appData.options.useProviderInterstitial && (hasProviders || appData.options.alwaysDisplayInterstitial);

		const customClasses = this.getCustomClasses(screening);
		const contentClasses = joinClasses('screening', s.screening, customClasses);
		const displayContent = [
			<div class={s.iconWrapper}><svg.SvgShowtimes class={s.icon} /></div>,
			<span class={joinClasses('time', s.time)}>{time}</span>
		];

		let display = null;
		if (screening.cta === 'x') {
			display = null;
		} else if (useAuth) {
			display = this.renderAuthButton(contentClasses, displayContent);
		} else if (useDirectTicketing && !hasProviders && time && !availableInterstitial) {
			display = this.renderDirectTicketingButton(contentClasses, displayContent);
		} else if (url && !pageData.disableUrls) {
			if (availableInterstitial) {
				display = this.renderInterstitialButton(contentClasses, displayContent, url);
			} else {
				display = this.renderDirectLink(contentClasses, displayContent, url);
			}
		} else if (time) {
			display = <div class={s.message}><span class={s.time}>{time}</span></div>;
		} else {
			display = <div class={s.message}><span class={s.time}>Unavailable</span></div>;
		}

		return <div id={'s' + screening.id} class={classes}>{display}</div>;
	}

}
